import {create} from 'zustand';
import {ServiceProvider} from '../models/serviceProvider.model';

interface ServiceProvidersUIState {
  // Modal States
  showAddForm: boolean;
  showUpdateForm: boolean;
  showDetailsModal: boolean;

  // Selected Items
  selectedProviderId: string | null;
  selectedProvider: ServiceProvider | null;

  // Modal Actions
  openAddForm: () => void;
  closeAddForm: () => void;
  openUpdateForm: (provider: ServiceProvider) => void;
  closeUpdateForm: () => void;
  openDetailsModal: (id: string) => void;
  closeDetailsModal: () => void;

  // Add Form State
  addFormData: {
    username: string;
    fullName: string;
    email: string;
    phoneNumber: string;
    password: string;
    companyName: string;
    website: string;
    description: string;
  };
  addFormErrors: Record<string, string>;
  addFormLoading: boolean;
  addFormShowPassword: boolean;

  // Add Form Actions
  setAddFormData: (data: Partial<ServiceProvidersUIState['addFormData']>) => void;
  setAddFormErrors: (errors: Record<string, string>) => void;
  setAddFormLoading: (loading: boolean) => void;
  setAddFormShowPassword: (show: boolean) => void;
  resetAddForm: () => void;

  // Update Form State
  updateFormData: {
    username: string;
    fullName: string;
    email: string;
    phoneNumber: string;
    companyName: string;
    website: string;
    description: string;
  };
  updateFormErrors: Record<string, string>;
  updateFormLoading: boolean;

  // Update Form Actions
  setUpdateFormData: (data: Partial<ServiceProvidersUIState['updateFormData']>) => void;
  setUpdateFormErrors: (errors: Record<string, string>) => void;
  setUpdateFormLoading: (loading: boolean) => void;
  resetUpdateForm: () => void;

  // Utility Actions
  reset: () => void;
}

const initialAddFormData = {
  username: '',
  fullName: '',
  email: '',
  phoneNumber: '',
  password: '',
  companyName: '',
  website: '',
  description: ''
};

const initialUpdateFormData = {
  username: '',
  fullName: '',
  email: '',
  phoneNumber: '',
  companyName: '',
  website: '',
  description: ''
};

export const useServiceProvidersUIStore = create<ServiceProvidersUIState>((set) => ({
  // Modal States
  showAddForm: false,
  showUpdateForm: false,
  showDetailsModal: false,

  // Selected Items
  selectedProviderId: null,
  selectedProvider: null,

  // Modal Actions
  openAddForm: () => set({showAddForm: true}),
  closeAddForm: () => set({showAddForm: false}),
  openUpdateForm: (provider) => set({
    showUpdateForm: true,
    selectedProvider: provider,
    updateFormData: {
      username: provider.username,
      fullName: provider.fullName,
      email: provider.email,
      phoneNumber: provider.phoneNumber,
      companyName: provider.companyName,
      website: provider.website || '',
      description: provider.description || ''
    }
  }),
  closeUpdateForm: () => set({
    showUpdateForm: false,
    selectedProvider: null
  }),
  openDetailsModal: (id) => set({
    showDetailsModal: true,
    selectedProviderId: id
  }),
  closeDetailsModal: () => set({
    showDetailsModal: false,
    selectedProviderId: null
  }),

  // Add Form State
  addFormData: initialAddFormData,
  addFormErrors: {},
  addFormLoading: false,
  addFormShowPassword: false,

  // Add Form Actions
  setAddFormData: (data) => set((state) => ({
    addFormData: {...state.addFormData, ...data}
  })),
  setAddFormErrors: (errors) => set({addFormErrors: errors}),
  setAddFormLoading: (loading) => set({addFormLoading: loading}),
  setAddFormShowPassword: (show) => set({addFormShowPassword: show}),
  resetAddForm: () => set({
    addFormData: initialAddFormData,
    addFormErrors: {},
    addFormLoading: false,
    addFormShowPassword: false
  }),

  // Update Form State
  updateFormData: initialUpdateFormData,
  updateFormErrors: {},
  updateFormLoading: false,

  // Update Form Actions
  setUpdateFormData: (data) => set((state) => ({
    updateFormData: {...state.updateFormData, ...data}
  })),
  setUpdateFormErrors: (errors) => set({updateFormErrors: errors}),
  setUpdateFormLoading: (loading) => set({updateFormLoading: loading}),
  resetUpdateForm: () => set({
    updateFormData: initialUpdateFormData,
    updateFormErrors: {},
    updateFormLoading: false
  }),

  // Utility Actions
  reset: () => set({
    showAddForm: false,
    showUpdateForm: false,
    showDetailsModal: false,
    selectedProviderId: null,
    selectedProvider: null,
    addFormData: initialAddFormData,
    addFormErrors: {},
    addFormLoading: false,
    addFormShowPassword: false,
    updateFormData: initialUpdateFormData,
    updateFormErrors: {},
    updateFormLoading: false
  })
}));
